import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkle, ArrowLeft, ArrowRight, FilePdf, EnvelopeSimple, WhatsappLogo, Target, ChartBar, Compass, ListChecks, ClipboardText, GraduationCap } from "@phosphor-icons/react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "caminhos_anamnesis_draft_v1";

const QUESTIONS = [
  { key: "objetivo", label: "Qual é seu principal objetivo?", options: ["ENEM","Vestibular","Concurso público","Concurso militar","Faculdade/graduação","Melhorar meu desempenho acadêmico","Ainda não tenho um objetivo definido"] },
  { key: "prazo", label: "Em quanto tempo você pretende alcançar esse objetivo?", options: ["Até 3 meses","3 a 6 meses","6 a 12 meses","Mais de 1 ano","Ainda não sei"] },
  { key: "horas", label: "Quantas horas por dia você consegue estudar de forma realista?", options: ["Menos de 1 hora","1 a 2 horas","2 a 4 horas","4 a 6 horas","Mais de 6 horas"] },
  { key: "organizacao", label: "Como você organiza seus estudos?", options: ["Tenho cronograma e sigo regularmente","Tenho cronograma, mas nem sempre sigo","Planejo apenas o dia","Estudo quando consigo","Não tenho organização definida"] },
  { key: "procrastinacao", label: "Com que frequência você deixa os estudos para depois?", options: ["Quase nunca","Às vezes","Frequentemente","Quase sempre"] },
  { key: "concentracao", label: "Quanto tempo consegue permanecer concentrado?", options: ["Menos de 20 minutos","20 a 40 minutos","40 a 60 minutos","1 a 2 horas","Mais de 2 horas"] },
  { key: "distracao", label: "O que mais atrapalha sua concentração?", options: ["Celular/redes sociais","Cansaço/sono","Ambiente","Ansiedade/preocupações","Falta de interesse","Dificuldade com o conteúdo","Interrupções","Nada específico"] },
  { key: "metodo", label: "Como normalmente estuda um conteúdo novo?", options: ["Leio ou assisto à aula e faço anotações","Leio e faço resumos","Assisto às aulas e resolvo questões","Faço mapas mentais/esquemas","Tento explicar o conteúdo com minhas próprias palavras","Releio várias vezes","Não tenho método definido"] },
  { key: "revisao", label: "Como costuma revisar?", options: ["Tenho revisões programadas","Reviso semanalmente","Reviso antes das provas/simulados","Reviso quando percebo que esqueci","Quase nunca reviso"] },
  { key: "questoes", label: "Qual sua relação com exercícios e questões?", options: ["Resolvo muitas questões e analiso meus erros","Resolvo regularmente","Resolvo algumas","Resolvo poucas","Quase não resolvo"] },
  { key: "erro", label: "Quando erra uma questão, o que faz?", options: ["Analiso o erro e identifico o motivo","Vejo a resposta e sigo","Tento novamente","Apenas marco como errada","Não costumo revisar meus erros"] },
  { key: "disciplina", label: "Como avalia sua disciplina? (1 a 5)", options: ["1 — Muito baixa","2","3","4","5 — Muito alta"], scale: true },
  { key: "sem_vontade", label: "Quando não está com vontade de estudar:", options: ["Estudo mesmo assim","Estudo menos tempo","Adio e tento depois","Acabo não estudando","Depende do dia"] },
  { key: "nao_entende", label: "Quando não entende um conteúdo:", options: ["Procuro outra explicação","Faço exercícios mais fáceis","Peço ajuda","Tento sozinho até entender","Deixo para depois","Desisto do conteúdo"] },
  { key: "frase", label: "Qual frase mais representa sua situação?", options: [
    "Estudo bastante, mas o resultado não corresponde ao esforço",
    "Tenho dificuldade para manter uma rotina",
    "Tenho dificuldade para começar",
    "Começo bem, mas perco consistência",
    "Tenho dificuldade para aprender/memorizar",
    "Tenho dificuldade para organizar o que estudar",
    "Sei o que fazer, mas tenho dificuldade para executar",
    "Estou indo bem e quero otimizar",
    "Não sei qual é minha principal dificuldade",
  ]},
  // ---- Módulo Aptidão Acadêmica (adição incremental — indexes 15,16,17) ----
  { key: "aa_tempo", label: "Em qual tipo de conteúdo você normalmente precisa gastar MAIS TEMPO para conseguir aprender?", options: [
    "Matemática, cálculos e raciocínio lógico",
    "Física, química e Ciências da Natureza",
    "História, geografia, filosofia e sociologia",
    "Português, literatura e interpretação de textos",
    "Tenho dificuldade semelhante em todas as áreas",
    "Não sei dizer",
  ]},
  { key: "aa_facilidade", label: "Qual tipo de conteúdo você normalmente aprende COM MAIS FACILIDADE?", options: [
    "Matemática, cálculos e raciocínio lógico",
    "Física, química e Ciências da Natureza",
    "História, geografia, filosofia e sociologia",
    "Português, literatura e interpretação de textos",
    "Tenho facilidade semelhante em todas as áreas",
    "Não sei dizer",
  ]},
  { key: "aa_preferencia", label: "Qual tipo de conteúdo você MAIS GOSTA ou teria maior disposição para estudar?", options: [
    "Matemática, cálculos e raciocínio lógico",
    "Física, química e Ciências da Natureza",
    "História, geografia, filosofia e sociologia",
    "Português, literatura e interpretação de textos",
    "Não tenho preferência",
    "Não sei dizer",
  ]},
];

const INDICATORS = [
  { key: "organizacao", label: "Organização", color: "#1E5FFF" },
  { key: "consistencia", label: "Consistência", color: "#7A5CFF" },
  { key: "concentracao", label: "Concentração", color: "#12B76A" },
  { key: "estrategias", label: "Estratégias", color: "#F79009" },
  { key: "revisao", label: "Revisão", color: "#FF3D71" },
  { key: "questoes", label: "Questões", color: "#B45309" },
  { key: "autorregulacao", label: "Autorregulação", color: "#0EA5E9" },
  { key: "motivacao", label: "Motivação", color: "#DB2777" },
];

export default function AnamnesisPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canRespond = user?.role === "student";
  const [stage, setStage] = useState("intro"); // intro | form | processing | result | history
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}");
      const arr = Array.isArray(d.answers) ? d.answers : [];
      const filled = Array(QUESTIONS.length).fill(null);
      for (let i = 0; i < Math.min(arr.length, QUESTIONS.length); i++) filled[i] = arr[i];
      return filled;
    } catch { return Array(QUESTIONS.length).fill(null); }
  });
  const [extra, setExtra] = useState(() => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}").extra || ""; } catch { return ""; } });
  const [result, setResult] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [students, setStudents] = useState([]);
  const [emailModal, setEmailModal] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [waModal, setWaModal] = useState(null);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ answers, extra }));
  }, [answers, extra]);

  async function loadHistory() {
    try {
      const r = await api.get("/anamnesis");
      setItems(r.data);
      if (user?.role !== "student") {
        const s = await api.get("/students");
        setStudents(s.data);
      }
    } catch (e) { console.warn("anamnesis loadHistory failed:", e); }
  }
  useEffect(() => { loadHistory(); }, [user?.id]);

  const q = QUESTIONS[step];
  const progress = Math.round(((step + 1) / QUESTIONS.length) * 100);
  const canNext = answers[step] != null;
  const canSubmit = useMemo(() => answers.every(v => v != null), [answers]);

  function pick(i) { setAnswers(a => a.map((v, idx) => idx === step ? i : v)); }
  function next() { if (step < QUESTIONS.length - 1) setStep(s => s + 1); }
  function prev() { if (step > 0) setStep(s => s - 1); }
  function reset() {
    setAnswers(Array(QUESTIONS.length).fill(null)); setExtra(""); setStep(0); setResult(null); setStage("intro");
    localStorage.removeItem(DRAFT_KEY);
  }

  async function submit() {
    if (!canSubmit) { toast.error("Responda todas as perguntas"); return; }
    setStage("processing");
    try {
      const payload = { answers, extra };
      if (user?.role !== "student") payload.student_id = selectedStudent;
      const r = await api.post("/anamnesis", payload);
      setResult(r.data);
      localStorage.removeItem(DRAFT_KEY);
      setStage("result");
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao processar");
      setStage("form");
    }
  }

  function openPdf(a) {
    const url = `${API_BASE}/anamnesis/${a.id}/pdf`;
    window.open(url, "_blank");
  }

  async function sendEmail(a) {
    setEmailTo(a.student_email || "");
    setEmailModal(a);
  }
  async function confirmSendEmail() {
    try {
      await api.post(`/anamnesis/${emailModal.id}/send-email`, { to: emailTo });
      toast.success(`Enviado para ${emailTo}`);
      setEmailModal(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro ao enviar"); }
  }

  async function openWa(a) {
    try {
      const r = await api.get(`/anamnesis/${a.id}/whatsapp-links`);
      setWaModal(r.data);
    } catch { toast.error("Erro ao gerar link"); }
  }

  // ---------- Renders ----------
  if (stage === "intro") {
    return (
      <div className="p-4 sm:p-8 lg:p-10 max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Meu Perfil de Estudos</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#12143A] mt-2">Descubra seu perfil de estudos</h1>
        <p className="text-zinc-600 mt-3 leading-relaxed">
          Responda algumas perguntas sobre sua rotina. Vamos identificar seus principais pontos fortes, dificuldades e qual estratégia pode ajudar você a estudar melhor.
        </p>
        {user?.role === "student" && user?.access_unlocked === false && (
          <div data-testid="locked-banner" className="mt-5 rounded-3xl border border-[#F79009]/30 bg-gradient-to-br from-[#FFF6EA] to-white p-5 sm:p-6 flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white border border-[#F79009]/30 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkle size={20} weight="fill" className="text-[#B45309]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#B45309]">Primeiro acesso</div>
              <div className="font-heading font-black text-[#12143A] mt-1">
                {items.length > 0
                  ? "Anamnese enviada! Aguardando liberação do seu professor."
                  : "Comece pela anamnese"}
              </div>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                {items.length > 0
                  ? "Seu professor revisará seu perfil de estudos e liberará o restante da plataforma (agenda, materiais, aulas, tutor, planos)."
                  : "Enquanto seu professor não libera as demais áreas, esta é a única etapa disponível. Responda 15 perguntas rápidas e receba um diagnóstico personalizado."}
              </p>
            </div>
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {canRespond ? (
            <Button data-testid="anam-start" onClick={() => setStage("form")} className="h-12 rounded-full gradient-primary text-white px-6">
              <Sparkle size={16} weight="fill" className="mr-2" /> Começar avaliação
            </Button>
          ) : (
            <div className="w-full">
              <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">Selecione um aluno para responder</div>
              <select data-testid="anam-student" value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}
                      className="h-11 rounded-2xl border border-zinc-200 px-3 bg-white w-full max-w-md">
                <option value="">— escolha —</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.name} • {s.email}</option>)}
              </select>
              <div className="mt-3">
                <Button disabled={!selectedStudent} onClick={() => setStage("form")} className="h-12 rounded-full gradient-primary text-white px-6">
                  <Sparkle size={16} weight="fill" className="mr-2" /> Começar avaliação
                </Button>
              </div>
            </div>
          )}
          {items.length > 0 && (
            <Button variant="outline" onClick={() => setStage("history")} className="h-12 rounded-full">
              <ClipboardText size={16} weight="fill" className="mr-2" /> Ver histórico ({items.length})
            </Button>
          )}
        </div>
        <p className="mt-8 text-[11px] text-zinc-400 leading-relaxed">Esta é uma avaliação educacional. Não constitui diagnóstico médico ou psicológico.</p>
      </div>
    );
  }

  if (stage === "form") {
    return (
      <div className="p-4 sm:p-8 lg:p-10 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="text-xs uppercase tracking-widest font-bold text-[#1E5FFF]">Pergunta {step + 1} de {QUESTIONS.length}</div>
          <button onClick={reset} className="text-xs text-zinc-500 hover:text-[#1E5FFF] underline underline-offset-2">Recomeçar</button>
        </div>
        <Progress value={progress} className="h-2" />
        <div className="mt-6 bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-sm">
          <h2 className="font-heading text-xl sm:text-2xl font-black text-[#12143A] leading-tight">{q.label}</h2>
          <div className="mt-5 space-y-2">
            {q.options.map((opt, i) => (
              <label key={i} data-testid={`opt-${step}-${i}`}
                     className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${
                       answers[step] === i ? "border-[#1E5FFF] bg-[#F4F8FF]" : "border-zinc-200 hover:border-zinc-300"}`}>
                <input type="radio" name={`q-${step}`} checked={answers[step] === i} onChange={() => pick(i)}
                       className="w-4 h-4 accent-[#1E5FFF]" />
                <span className="flex-1 text-sm text-[#12143A]">{opt}</span>
              </label>
            ))}
          </div>
          {step === QUESTIONS.length - 1 && (
            <div className="mt-5">
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Existe alguma dificuldade ou situação importante que você gostaria de contar? (opcional)</label>
              <Textarea data-testid="anam-extra" value={extra} onChange={e => setExtra(e.target.value)} rows={3} className="mt-2" />
            </div>
          )}
        </div>
        <div className="mt-5 flex items-center justify-between gap-3">
          <Button data-testid="anam-prev" variant="outline" onClick={prev} disabled={step === 0} className="h-11 rounded-full">
            <ArrowLeft size={14} className="mr-2" /> Voltar
          </Button>
          {step < QUESTIONS.length - 1 ? (
            <Button data-testid="anam-next" onClick={next} disabled={!canNext} className="h-11 rounded-full gradient-primary text-white px-6">
              Próxima <ArrowRight size={14} className="ml-2" />
            </Button>
          ) : (
            <Button data-testid="anam-submit" onClick={submit} disabled={!canSubmit} className="h-11 rounded-full gradient-primary text-white px-6">
              <Sparkle size={14} weight="fill" className="mr-2" /> Ver meu perfil
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (stage === "processing") {
    return (
      <div className="p-4 sm:p-8 lg:p-10 max-w-2xl mx-auto text-center py-24">
        <div className="w-16 h-16 rounded-3xl gradient-primary mx-auto mb-6 flex items-center justify-center animate-pulse">
          <Sparkle size={28} color="white" weight="fill" />
        </div>
        <h2 className="font-heading text-2xl font-black text-[#12143A]">Analisando seu perfil…</h2>
        <p className="text-zinc-500 mt-2">Estamos cruzando suas respostas para gerar um diagnóstico personalizado.</p>
      </div>
    );
  }

  if (stage === "result" && result) return <ResultView a={result} onNew={reset} onPdf={openPdf} onEmail={sendEmail} onWa={openWa} onBack={() => setStage("history")} />;

  if (stage === "history") {
    return (
      <div className="p-4 sm:p-8 lg:p-10 max-w-4xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Histórico</div>
            <h1 className="font-heading text-3xl font-black text-[#12143A] mt-2">Avaliações de perfil de estudos</h1>
          </div>
          <Button onClick={() => setStage("intro")} variant="outline" className="rounded-full"><ArrowLeft size={14} className="mr-2" /> Voltar</Button>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 rounded-3xl">Nenhuma avaliação registrada ainda.</div>
        ) : (
          <div className="grid gap-3">
            {items.map(a => (
              <button key={a.id} data-testid={`anam-item-${a.id}`} onClick={() => { setResult(a); setStage("result"); }}
                className="text-left bg-white border border-zinc-200 rounded-2xl p-4 hover:border-[#1E5FFF] transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0"><Compass size={20} color="white" weight="fill" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#12143A] truncate">{a.perfil_principal}{a.perfil_secundario ? ` • ${a.perfil_secundario}` : ""}</div>
                    <div className="text-xs text-zinc-500 truncate">{a.student_name} • {(a.created_at || "").slice(0, 10)}</div>
                  </div>
                  <ArrowRight size={16} className="text-zinc-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
}

// ------------- Result view -------------
function ScoreBar({ label, value, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs font-bold text-[#12143A] mb-1"><span>{label}</span><span className="font-mono-num" style={{ color }}>{value}/100</span></div>
      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: color }} /></div>
    </div>
  );
}

function ResultView({ a, onNew, onPdf, onEmail, onWa, onBack }) {
  const scores = a.scores || {};
  const procColor = a.procrastinacao === "alta" ? "#FF3D71" : a.procrastinacao === "moderada" ? "#F79009" : "#12B76A";
  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Seu Perfil de Estudos</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#12143A] mt-2">{a.perfil_principal}</h1>
          {a.perfil_secundario && <div className="text-sm text-zinc-500 mt-1">Perfil secundário: <b>{a.perfil_secundario}</b></div>}
        </div>
        <Button onClick={onBack} variant="outline" className="rounded-full h-10"><ArrowLeft size={14} className="mr-2" /> Voltar</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-2"><Target size={18} weight="fill" className="text-[#1E5FFF]" /><h3 className="font-heading font-black text-lg text-[#12143A]">Seu diagnóstico</h3></div>
        <p className="text-[15px] text-zinc-700 leading-relaxed" data-testid="anam-diag">{a.diagnostico}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-sm">
          <h3 className="font-heading font-black text-lg text-[#12143A] mb-3">✨ Pontos fortes</h3>
          <ul className="space-y-2">{(a.pontos_fortes || []).map((p, i) => <li key={i} className="flex gap-2 text-sm text-zinc-700"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#12B76A] flex-shrink-0" />{p}</li>)}</ul>
        </div>
        <div className="bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-sm">
          <h3 className="font-heading font-black text-lg text-[#12143A] mb-3">🎯 Principais gargalos</h3>
          <ul className="space-y-2">{(a.gargalos || []).map((p, i) => <li key={i} className="flex gap-2 text-sm text-zinc-700"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FF3D71] flex-shrink-0" />{p}</li>)}</ul>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><ChartBar size={18} weight="fill" className="text-[#1E5FFF]" /><h3 className="font-heading font-black text-lg text-[#12143A]">Seu mapa de estudos</h3></div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
          {INDICATORS.map(ind => <ScoreBar key={ind.key} label={ind.label} value={scores[ind.key] ?? 0} color={ind.color} />)}
        </div>
        <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest font-bold text-zinc-500">Procrastinação</span>
          <span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: procColor }}>{a.procrastinacao}</span>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#F4F8FF] via-white to-[#FFF6EA]/40 border border-[#1E5FFF]/20 rounded-3xl p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-2"><Compass size={18} weight="fill" className="text-[#1E5FFF]" /><h3 className="font-heading font-black text-lg text-[#12143A]">Seu principal foco</h3></div>
        <p className="text-[15px] text-[#12143A] leading-relaxed">Seu principal foco agora deve ser: <b>{a.principal_foco}</b>.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3"><ListChecks size={18} weight="fill" className="text-[#1E5FFF]" /><h3 className="font-heading font-black text-lg text-[#12143A]">Próximos 7 dias</h3></div>
        <ol className="space-y-2">{(a.acoes_7_dias || []).map((p, i) => (
          <li key={i} className="flex gap-3 text-sm text-zinc-700">
            <span className="w-6 h-6 rounded-lg gradient-sky text-[#1E5FFF] font-bold text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
            <span className="pt-0.5">{p}</span>
          </li>))}
        </ol>
      </div>

      {a.afinidade_academica && (
        <div data-testid="anam-aptidao" className="bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap size={18} weight="fill" className="text-[#1E5FFF]" />
            <h3 className="font-heading font-black text-lg text-[#12143A]">Afinidade Acadêmica</h3>
            <span className={`ml-auto text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${
              a.nivel_confianca_afinidade === "Alta" ? "bg-[#DCF7E5] text-[#12B76A]" :
              a.nivel_confianca_afinidade === "Média" ? "bg-[#FFF6EA] text-[#B45309]" :
              "bg-zinc-100 text-zinc-500"}`}>Confiança {a.nivel_confianca_afinidade}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded-2xl bg-[#F4F8FF] border border-[#1E5FFF]/15">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1E5FFF]">Maior afinidade</div>
              <div className="font-bold text-[#12143A] mt-1">{a.afinidade_academica}</div>
              {a.afinidade_secundaria && <div className="text-xs text-zinc-500 mt-0.5">Secundária: {a.afinidade_secundaria}</div>}
            </div>
            <div className="p-3 rounded-2xl bg-[#FFF6EA] border border-[#FFB86B]/40">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#B45309]">Maior esforço atual</div>
              <div className="font-bold text-[#12143A] mt-1">{a.area_maior_esforco || "—"}</div>
              <div className="text-xs text-zinc-500 mt-0.5">Perfil acadêmico: {a.perfil_academico || "—"}</div>
            </div>
          </div>
          {a.aptidao_interpretacao && <p className="text-[14px] text-zinc-700 leading-relaxed mt-3">{a.aptidao_interpretacao}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 sticky bottom-2 sm:static bg-white/80 backdrop-blur border border-zinc-200 rounded-full p-2 sm:p-0 sm:bg-transparent sm:border-0 sm:backdrop-blur-none">
        <Button data-testid="anam-pdf" onClick={() => onPdf(a)} variant="outline" className="rounded-full h-11"><FilePdf size={14} weight="fill" className="mr-1 text-[#FF3D71]" /> PDF</Button>
        <Button data-testid="anam-email" onClick={() => onEmail(a)} variant="outline" className="rounded-full h-11"><EnvelopeSimple size={14} weight="fill" className="mr-1 text-[#1E5FFF]" /> Enviar por e-mail</Button>
        <Button data-testid="anam-wa" onClick={() => onWa(a)} variant="outline" className="rounded-full h-11"><WhatsappLogo size={14} weight="fill" className="mr-1 text-[#25D366]" /> WhatsApp</Button>
        <Button onClick={onNew} className="rounded-full h-11 gradient-primary text-white ml-auto"><Sparkle size={14} weight="fill" className="mr-1" /> Nova avaliação</Button>
      </div>

      <p className="text-[11px] text-zinc-400 text-center leading-relaxed">Este relatório possui finalidade exclusivamente educacional e não constitui diagnóstico médico ou psicológico.</p>
    </div>
  );
}
